import { Observable } from 'rxjs';

/**
 * Raw JSON:API-like response returned by backend.
 * Used by `BazisSrvService` to transform server data into `EntData` / `EntList`.
 */
export type SrvResponse = {
    /** Entity item or list data in server format. */
    data: SrvEntData[] | SrvEntData;
    /** Meta object (pagination, actions, permissions, etc.). */
    meta: any;
    /** Included related entities (server format). */
    included: any[];
};

/**
 * Sparse fieldsets map used in requests.
 * Key is entity type, value is a list of allowed field names.
 */
export type SrvRequestFields = {
    [key: string]: string[];
};

/**
 * Minimal generic data object with id.
 * Used when you have simple objects and need to convert them to `EntData`.
 */
export type SimpleData = {
    id: string;
    [key: string]: any;
};

/**
 * JSON:API entity reference (type + id).
 * Used for relationships in server and client payloads.
 */
export type EntDescription = {
    /** Entity id string. */
    id: string;
    /** Entity type string (`foo.bar`). */
    type: string;
};

/**
 * Client-side entity model.
 *
 * This is the main shape used across services:
 * - `BazisEntityService` stores entities in `BazisStorageService`
 * - UI reads entity fields from `$snapshot`
 * - `BazisSrvService` can enrich entities with helper flags and meta-derived actions
 */
export type EntData = {
    /** Entity type string (`foo.bar`). */
    type: string;
    /** Entity id string. */
    id?: string;
    /** Included entities (already transformed to `EntData`). */
    included?: EntData[];
    /**
     * Flat snapshot object used by UI.
     * Usually contains `id`, `type`, attributes, and relationships flattened as `{ field: relationship.data }`.
     */
    $snapshot?: any;
    /** Backend meta information attached to the entity. */
    $meta?: {
        state_actions: any[];
    };
    /** Map of transit actions derived from meta state actions. */
    $transitMap?: {
        [index: string]: EntTransit;
    };
    /** Transit groups (array of arrays) derived from meta state actions. */
    $transits?: string[][];
    /** True when backend meta indicates the entity can be signed. */
    $canSign?: boolean;
    /** Signing context(s) derived from meta actions (string or list). */
    $signContext?: string | string[];
    /** Permission helper: can create items in list context. */
    $canAdd?: boolean;
    /** Permission helper: can change/update the entity. */
    $canChange?: boolean;
    /** Permission helper: can view the entity. */
    $canView?: boolean;
    /** Permission helper: can delete the entity. */
    $canDelete?: boolean;
    /** Optional timestamp used for sorting in UI. */
    $timestamp?: number;
};

/**
 * Client-side list model returned by list requests.
 */
export type EntList = {
    /** Optional list type (often same as requested entity type). */
    type?: string;
    /** List items. */
    list: EntData[];
    /** Permission helper: can create items for this list. */
    $canAdd?: boolean;
    /** Backend meta for list requests (pagination, allowed status filters, etc.). */
    $meta?: {
        pagination: any;
        status_allowed?: string[];
    };
};

/**
 * Transit action description derived from backend meta `state_actions`.
 * Used by UI to show available transit buttons and payload requirements.
 */
export type EntTransit = {
    /** Transit id/code string. */
    id: string;
    /** True when action is allowed (no critical restricts). */
    isAllowed: boolean;
    /** Raw restricts list from backend. */
    restricts: any[];
    /** Payload schema map used to render transit form (or null). */
    payload: { [index: string]: any };
    /** Optional hint text shown in UI (backend-provided). */
    hint: string;
    /** Endpoint URL for transit action. */
    url: string;
    /** True when current user signature is required. */
    needMySign: boolean;
    /** True when other signatures are required. */
    needOtherSign: boolean;
    /** UI hint: show sign button instead of transit when both signatures are needed. */
    showSignButtonInsteadOfTransit: boolean;
    /** Subset of restricts that UI should display. */
    restrictsToDisplay: any[];
    /** Unique restrict texts used to show concise messages. */
    uniqueRestrictTextsToDisplay: string[];
};

/**
 * Transit request payload sent to backend transit endpoints.
 */
export type EntTransitData = {
    /** Transit id/code string. */
    transit: string;
    /** Transit payload object (in practice this can be any, but current typing is string). */
    payload: string;
};

/**
 * Document signing settings used by document/sign services.
 * Produced/consumed by `BazisDocumentService` and `BazisSrvService`.
 */
export type EntDocumentSettings = {
    /** Entity type that owns the document to sign. */
    entityType: string;
    /** Entity id that owns the document to sign. */
    entityId: string;
    /** Optional signing context label (backend-specific). */
    contextLabel?: string;
    /** Optional payload sent when requesting signable document. */
    documentPayload?: any;
    /** Optional payload merged into sign body request. */
    signBodyPayload?: any;
    /** Optional entity/document data used by UI. */
    document?: EntData;
    /** If true, cancels previous signings before requesting a document. */
    cancelPreviousSignings?: boolean;
    /** If true, entity reload after signing should not include `documents`. */
    withoutIncludedDocuments?: boolean;
};

/**
 * UI helper type: one item to sign plus a `document$` stream for lazy loading.
 */
export type ItemToSign = EntDocumentSettings & {
    /** Observable that emits the signable document entity/data. */
    document$: Observable<EntData>;
};

/**
 * Client-side schema model for entity create/update/transit/retrieve/list schemas.
 * Produced by `BazisSrvService.fetchSchema$()` and cached by `BazisEntityService`/`BazisStorageService`.
 */
export type EntSchema = {
    /** Entity type string. */
    entityType: string;
    /** Entity id for id-specific schemas (can be empty). */
    id: string;
    /** Attributes schema map (backend-derived). */
    attributes: any;
    /** Relationships schema map (backend-derived). */
    relationships: any;
    /** Required fields list. */
    required: string[];
    /** Optional error code when schema request fails (example: 403). */
    error?: number;
    /** Schema type discriminator. */
    schemaType?: SchemaType;
    /** Included related schemas (for nested resources). */
    included?: EntSchema[];
};

/**
 * Priority configuration used by `BazisEntityService.setPriority$()`.
 */
export type EntPriority = {
    /** Priority id/key sent to backend. */
    id: string;
    /** Snapshot property name that should be patched in storage. */
    property: string;
    /** Priority value (truthy to add, falsy to remove). */
    value?: boolean;
    /** Default visibility flag for UI filters. */
    defaultVisibility?: boolean;
    /** Optional function to decide whether the priority filter is visible in UI. */
    isVisibleFn?: (filtersValue: any, value: any) => boolean;
};

/**
 * Supported schema type strings used across services.
 */
export type SchemaType =
    | 'schema_list'
    | 'schema_create'
    | 'schema_update'
    | 'schema_transit'
    | 'schema_retrieve';

/**
 * Raw schema response returned by backend before transformation into `EntSchema`.
 */
export type SrvSchema = {
    title: string;
    entityType: string;
    required?: string[];
    definitions?: {
        [index: string]: any;
    };
    $defs?: {
        [index: string]: any;
    };
    properties: {
        [index: string]: any;
    };
    error?: number;
};

/**
 * Raw server entity model in JSON:API-like format.
 * Converted to `EntData` by `BazisSrvService.makeupEntity()`.
 */
export type SrvEntData = {
    /** Entity type string. */
    type: string;
    /** Entity id string. */
    id: string;
    /** Attributes map. */
    attributes: {
        [index: string]: any;
    };
    /** Relationships map in server format. */
    relationships: SrvEntRelationshipsData;
    /** Meta map (server-provided). */
    meta: {
        [index: string]: any;
    };
};

/**
 * Relationships payload in server format.
 * Each relationship property holds `{ data: EntDescription | EntDescription[] }`.
 */
export type SrvEntRelationshipsData = {
    [index: string]: {
        data: EntDescription | EntDescription[];
    };
};

/**
 * JSON:API errors container returned by backend.
 */
export type SrvErrors = {
    errors: SrvError[];
};

/**
 * Single JSON:API error item.
 * Used by `BazisSrvService.generateErrorMessage()` and other error handling logic.
 */
export type SrvError = {
    status: string;
    title?: string;
    code: string;
    source?: {
        pointer: string;
        parameter: string;
    };
    detail: string;
    meta: any;
};

/**
 * Search request settings used by some search helpers, infinite lists
 */
export type SearchSettings = {
    entityType: string;
    fieldForId?: string;
    suffix?: string;
    params?: { [index: string]: string };
    fields?: string[];
    limit?: number;
    paramsGenerator?: Function;
};

/**
 * Single item for bulk/group request (`BazisSrvService.groupRequest$()`).
 */
export type GroupRequestItem = {
    entityType: string;
    entityId?: string;
    requestType: 'list' | 'item' | 'any';
    meta: string[];
    limit?: number;
    offset?: number;
    sort?: number;
    params: Object;
    body?: Object;
    method?: 'PATCH' | 'POST' | 'GET' | 'DELETE';
};

/**
 * Media description for UI uploads/previews.
 */
export type MediaDescription = {
    src: string;
    type: 'image' | 'video' | 'plot';
    name?: string;
    data?: ArrayBuffer;
    alt?: string;
    isLoaded?: boolean;
};

/**
 * Included entity data settings used to build JSON:API `included` section in create/update requests.
 */
export type IncludedEntData = {
    id: string;
    entityType: string;
    action: string;
    attributes: any;
    relationships: relationshipsEntData;
};

/**
 * Relationships object used in create/update helpers.
 * Values can be ids, ids arrays, or full `{ type, id }` descriptions.
 */
export type relationshipsEntData = {
    [index: string]: string | string[] | EntDescription | EntDescription[];
};

/**
 * Parsed socket update data produced by `BazisEntitySocketUpdateService`.
 */
export type SocketUpdateData = {
    entityType: string;
    entityId: string;
    event: string;
    payload?: SocketUpdatePayload;
};

/**
 * Socket update payload that may contain partial changes.
 */
export type SocketUpdatePayload = {
    /** Partial attributes update (merged into `$snapshot`). */
    attributes?: any;
    /** Partial relationships update (flattened into `$snapshot`). */
    relationships?: any;
    /** Additional required fields/context (backend-specific). */
    context?: any;
};
